import React, { Fragment } from "react";
import { Card, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Bookmark } from "lucide-react";
import { SavedViewListItem, useSavedViews } from "@/hooks/saved-views";
import { useSavedViewManagement } from "Components/SavedViews/useSavedViewManagement";
import { SavedViewMenu } from "Components/SavedViews/SavedViewMenu";
import { Utils } from "@/common/utils";

/** Navigates to the view's dashboard with the saved view applied via query param. */
function viewLink(view: SavedViewListItem): string {
    return `${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(view.dashboard_name)}?savedViewId=${view.ruuid}`;
}

function formatSaved(view: SavedViewListItem): string {
    const date = new Date(view.enflux_created);
    return isNaN(date.getTime()) ? "" : Utils.formatDate(date, "MM/DD/YYYY");
}

/**
 * Home page panel listing every saved view the user can access across all
 * dashboards. Clicking a row navigates to its dashboard with the view applied;
 * each row's `···` menu offers Rename / Update (disabled here) / Share / Remove.
 */
export default function SavedViewsHomePanel() {
    const { data: views, error, isLoading } = useSavedViews();
    // No getCurrentUrl here — there is no live dashboard, so "Update with
    // Current State" is shown but disabled.
    const mgmt = useSavedViewManagement();

    return (
        <Card className="rounded-4">
            <Card.Header className="bg-light rounded-top-4 d-flex justify-content-between align-items-center py-2 px-3">
                <h6 className="mb-0 mt-1 fw-bold" style={{ fontSize: "18px", lineHeight: "26px" }}>
                    Saved Views
                </h6>
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                        backgroundColor: "#E8E8E8",
                        border: "1px solid #D8D8D8",
                        padding: "8px",
                        width: "36px",
                        height: "36px",
                    }}
                >
                    <Bookmark size={18} />
                </div>
            </Card.Header>

            <Card.Body className="px-3" style={{ maxHeight: "270px", overflowY: "auto" }}>
                {isLoading && !error && (
                    <div className="py-3 text-center">
                        <Spinner variant="secondary" />
                    </div>
                )}

                {!isLoading && error && (
                    <div className="text-center py-4 text-danger">Could not load saved views.</div>
                )}

                {!isLoading && !error && views && views.length === 0 && (
                    <div className="text-center py-5 my-4" data-test-id="home-saved-views-empty">
                        <h3 className="h5 mb-3">No saved views</h3>
                        <p className="text-muted mx-auto" style={{ maxWidth: "400px" }}>
                            You haven&apos;t saved any dashboard views yet. Save a view from any dashboard to see it here.
                        </p>
                    </div>
                )}

                {!isLoading && !error && views && views.length > 0 && (
                    <div className="d-flex flex-column" data-test-id="home-saved-views-list">
                        {views.map((view, index) => (
                            <Fragment key={view.ruuid}>
                                {index > 0 && <hr className="my-2 border-secondary-subtle" />}
                                <div className="d-flex justify-content-between align-items-center gap-2">
                                    <Link
                                        to={viewLink(view)}
                                        className="text-black text-decoration-none hover-transition rounded-3 flex-grow-1 py-2 px-1"
                                        data-test-id={`home-saved-view-${view.ruuid}`}
                                        title={view.description || view.name}
                                    >
                                        <span className="font-base-size fw-semibold d-block text-truncate">{view.name}</span>
                                        <span className="text-muted small">
                                            {view.dashboard_name}
                                            {formatSaved(view) && ` · ${formatSaved(view)}`}
                                        </span>
                                    </Link>

                                    {mgmt.updatingId === view.ruuid && <Spinner animation="border" size="sm" />}

                                    <SavedViewMenu
                                        view={view}
                                        onRename={mgmt.openRename}
                                        onUpdate={mgmt.updateWithCurrentState}
                                        canUpdate={mgmt.canUpdate}
                                        onShare={mgmt.openShare}
                                        onRemove={mgmt.openRemove}
                                    />
                                </div>
                            </Fragment>
                        ))}
                    </div>
                )}
            </Card.Body>

            {mgmt.modals}
        </Card>
    );
}
